import { useRef, useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useDatasetStore, effectiveDashboardName } from '@/store/dataset-store';
import { sanitizeFileName } from '@/lib/sanitize-filename';
import { useWidgetsStore } from '@/store/widgets-store';
import { usePinnedFiltersStore } from '@/store/pinned-filters-store';
import { useImportConfigStore } from '@/store/import-config-store';
import { useModeStore } from '@/store/mode-store';
import { useCompareConfigStore } from '@/store/compare-config-store';
import { useUiStore } from '@/store/ui-store';
import type { DashboardConfig } from '@/types/dashboard-config';
import { configDb } from '@/persistence/config-db';
import { getQueryWorker } from '@/engine/worker-client';
import { downloadJson, exportDashboard } from './apply';
import { parseConfig } from './import-config';
import { ImportReviewModal } from './ImportReviewModal';

/**
 * Sidebar pair: export the current dashboard to a JSON file,
 * import a JSON file into the current dataset (via review modal).
 */
export function SaveLoadButtons(): JSX.Element | null {
  const { t } = useTranslation();
  const activeId = useDatasetStore((s) => s.activeDatasetId);
  const datasets = useDatasetStore((s) => s.datasets);
  const setValueGroupings = useDatasetStore((s) => s.setValueGroupings);
  const applyOverrides = useDatasetStore((s) => s.applyOverrides);
  const setDatasetProfile = useDatasetStore((s) => s.setDatasetProfile);
  const loadWidgets = useWidgetsStore((s) => s.loadForDataset);
  const loadPinned = usePinnedFiltersStore((s) => s.loadForDataset);
  const loadCompareConfig = useCompareConfigStore((s) => s.loadForDataset);
  const mode = useModeStore((s) => s.mode);

  const setActiveModal = useUiStore((s) => s.setActiveModal);
  const importModalOpen = useUiStore((s) => s.activeModal === 'import-review');

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importing, setImporting] = useState<DashboardConfig | null>(null);
  const [error, setError] = useState<string | null>(null);

  const entry = activeId ? datasets.get(activeId) : undefined;
  if (!entry || !activeId) return null;

  async function handleExport(): Promise<void> {
    if (!entry || !activeId) return;
    try {
      const { json } = await exportDashboard({
        datasetId: activeId,
        currentColumns: entry.columns,
        columnOverrides: entry.overrides ?? {},
        valueGroupings: entry.valueGroupings ?? {},
      });
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-');
      const base = sanitizeFileName(effectiveDashboardName(entry)) || 'dashboard';
      downloadJson(`${base}-${stamp}.json`, json);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('export.unknownError');
      setError(`${t('export.failed', { message: msg })}`);
    }
  }

  async function handleFilePicked(
    e: ChangeEvent<HTMLInputElement>,
  ): Promise<void> {
    const file = e.target.files?.[0];
    // Reset input so the same file can be re-selected if the user cancels.
    e.target.value = '';
    if (!file) return;
    setError(null);
    try {
      const text = await file.text();
      const config = parseConfig(text);
      setImporting(config);
      setActiveModal('import-review');
      // Store configuration so 'Eşleşmeleri kontrol et' can reopen later.
      useImportConfigStore.getState().setLastConfig(config);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('export.unknownError');
      setError(msg);
    }
  }

  async function handleImported(): Promise<void> {
    if (!entry || !activeId) return;
    // Reload everything that changed for the active mode (widgets +
    // pinned filters); the inactive mode rehydrates next time the
    // user toggles into it. Also reload the compare config so the
    // banner / panel reflect any imported period selection.
    await loadWidgets(activeId, entry.columns, mode);
    await loadPinned(activeId, mode, {});
    await loadCompareConfig(activeId);
    // Apply imported column overrides — this rebuilds the typed view so
    // column types match what the exported config expects. Without this,
    // value groupings on override-dependent columns silently fail.
    try {
      const co = await configDb.columnOverrides.get(activeId);
      if (co && Object.keys(co.overrides).length > 0) {
        const api = getQueryWorker();
        const preview = await api.applyColumnOverrides(activeId, co.overrides);
        applyOverrides(activeId, preview, co.overrides);
        // Background re-profile (mirrors UploadButton pattern).
        api
          .profileDataset(activeId)
          .then((profile) => setDatasetProfile(activeId, profile))
          .catch((err: unknown) => {
            console.warn('profileDataset (post-import override) failed', err);
          });
      }
    } catch (err) {
      console.warn('columnOverrides import-apply failed', err);
    }
    // Re-pull value groupings from Dexie (see App.tsx for the same pattern).
    try {
      const vg = await configDb.valueGroupings.get(activeId);
      setValueGroupings(activeId, vg?.groupings ?? {});
    } catch {
      setValueGroupings(activeId, {});
    }
  }

  return (
    <>
      <button
        type="button"
        onClick={() => void handleExport()}
        className="w-full text-xs px-2.5 py-2 rounded-md hover:opacity-80 text-left"
        style={{
          color: 'var(--color-fg)',
          background: 'var(--color-hover)',
          border: '1px solid var(--color-border)',
        }}
      >
        {t('sidebar.button.exportJson')}
      </button>
      <button
        type="button"
        onClick={() => fileInputRef.current?.click()}
        className="w-full text-xs px-2.5 py-2 rounded-md hover:opacity-80 text-left"
        style={{
          color: 'var(--color-fg)',
          background: 'var(--color-hover)',
          border: '1px solid var(--color-border)',
        }}
      >
        {t('sidebar.button.importJson')}
      </button>
      <input
        ref={fileInputRef}
        type="file"
        accept="application/json,.json"
        className="hidden"
        onChange={(e) => void handleFilePicked(e)}
      />
      {error && (
        <span
          className="text-[11px] px-1"
          style={{ color: 'var(--color-danger, #ef4444)' }}
          role="alert"
        >
          {error}
        </span>
      )}

      {importing && importModalOpen && (
        <ImportReviewModal
          open
          onClose={() => {
            setImporting(null);
            setActiveModal(null);
          }}
          config={importing}
          targetDatasetId={activeId}
          currentColumns={entry.columns}
          onImported={() => void handleImported()}
        />
      )}
    </>
  );
}
